<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Shopee"
    title="Pre-Alerts"
    subtitle="Inbound Shopee vessel shipments. Scan progress updates in real time."
  >
    <div actions class="flex items-center gap-2">
      @if (!(isShopeeChecker$ | async)) {
        <button hlmBtn variant="outline" size="sm"
                (click)="exportData()"
                [disabled]="isLoading || preAlerts.length === 0">
          <ng-icon hlm name="lucideDownload" class="mr-1.5 size-3.5" />
          Export
        </button>
        <button hlmBtn size="sm"
                (click)="openExcelImport()"
                [disabled]="isLoading">
          <ng-icon hlm name="lucideUpload" class="mr-1.5 size-3.5" />
          Import LM
        </button>
      }
      <button hlmBtn variant="ghost" size="sm"
              (click)="refresh()"
              [disabled]="isLoading"
              title="Refresh">
        <ng-icon hlm name="lucideRefreshCw"
                 class="size-3.5"
                 [class.animate-spin]="isLoading" />
      </button>
    </div>
  </app-page-header>

  <!-- Error alert -->
  @if (error) {
    <app-inline-alert tone="danger">
      {{ error }}
      <button hlmBtn variant="ghost" size="sm"
              class="ml-2 h-6 text-xs"
              (click)="error = null">
        Dismiss
      </button>
    </app-inline-alert>
  }

  <!-- ── Main table card ── -->
  <div hlmCard>

    <!-- Toolbar row -->
    <div class="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">

      <!-- Search -->
      <div class="flex-1 min-w-[240px] max-w-md relative">
        <ng-icon hlm name="lucideSearch"
                 class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 pointer-events-none text-muted-foreground" />
        <input hlmInput
               type="text"
               class="pl-8 h-9 w-full"
               placeholder="Search lading bill, vessel, transport company…"
               [(ngModel)]="searchTerm"
               (input)="onSearch()"
               (keyup.enter)="onSearch()" />
      </div>

      <!-- Status filter — canonical dropdown -->
      <div class="relative">
        <select class="appearance-none h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent w-36"
                [(ngModel)]="filterForm.status"
                title="Filter by status">
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="InTransit">In Transit</option>
          <option value="Arrived">Arrived</option>
          <option value="Cleared">Cleared</option>
          <option value="Delivered">Delivered</option>
        </select>
        <ng-icon hlm name="lucideChevronDown"
                 class="absolute right-2 top-1/2 -translate-y-1/2 size-4 pointer-events-none text-muted-foreground" />
      </div>

      <!-- Advanced filters toggle -->
      <button hlmBtn variant="outline" size="sm"
              (click)="toggleAdvancedFilters()">
        <ng-icon hlm name="lucideSlidersHorizontal" class="mr-1.5 size-3.5" />
        {{ showAdvancedFilters ? 'Less' : 'More' }} filters
      </button>

      @if (filterForm.lading_bill || filterForm.transport_company || filterForm.vessel_no ||
           filterForm.arrival_location || filterForm.departure_location || filterForm.status ||
           filterForm.eta_from || filterForm.eta_to || searchTerm) {
        <button hlmBtn variant="ghost" size="sm" (click)="clearFilters()">
          <ng-icon hlm name="lucideX" class="mr-1.5 size-3.5" />
          Clear
        </button>
      }

      <div class="flex-1"></div>

      <!-- Pagination size picker — canonical dropdown -->
      <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>Per page:</span>
        <div class="relative">
          <select class="appearance-none h-7 border border-border-strong bg-surface rounded-md pl-2 pr-7 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent w-16"
                  [(ngModel)]="pageSize"
                  (change)="onPageSizeChange(pageSize)">
            <option [value]="10">10</option>
            <option [value]="20">20</option>
            <option [value]="50">50</option>
            <option [value]="100">100</option>
          </select>
          <ng-icon hlm name="lucideChevronDown"
                   class="absolute right-1.5 top-1/2 -translate-y-1/2 size-3 pointer-events-none text-muted-foreground" />
        </div>
      </div>

    </div>

    <!-- Advanced filter panel -->
    @if (showAdvancedFilters) {
      <div class="px-4 py-3 border-b border-border/60 grid grid-cols-2 md:grid-cols-4 gap-3 bg-surface-raised">
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">Lading Bill</label>
          <input hlmInput type="text" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.lading_bill" placeholder="Enter lading bill" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">Transport Company</label>
          <input hlmInput type="text" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.transport_company" placeholder="Company name" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">Vessel No</label>
          <input hlmInput type="text" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.vessel_no" placeholder="Vessel number" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">Arrival Location</label>
          <input hlmInput type="text" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.arrival_location" placeholder="Arrival location" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">Departure Location</label>
          <input hlmInput type="text" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.departure_location" placeholder="Departure location" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">ETA From</label>
          <input hlmInput type="date" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.eta_from" />
        </div>
        <div>
          <label class="block text-[10.5px] font-medium uppercase tracking-wider mb-1 text-muted-foreground">ETA To</label>
          <input hlmInput type="date" class="h-8 text-xs w-full"
                 [(ngModel)]="filterForm.eta_to" />
        </div>
        <div class="flex items-end gap-2">
          <button hlmBtn size="sm" class="h-8 text-xs flex-1" (click)="applyFilters()">
            Apply
          </button>
          <button hlmBtn variant="outline" size="sm" class="h-8 text-xs flex-1" (click)="clearFilters()">
            Clear
          </button>
        </div>
      </div>
    }

    <!-- Loading -->
    @if (isLoading && preAlerts.length === 0) {
      <div class="p-4">
        <app-skeleton [rows]="5" />
      </div>

    <!-- Empty state -->
    } @else if (!isLoading && preAlerts.length === 0 && !error) {
      <app-empty-state
        icon="lucideInbox"
        heading="No Pre-Alerts Found"
        [description]="searchTerm ? 'No pre-alerts match your search criteria.' : 'No pre-alerts have been received yet.'"
      >
        @if (searchTerm) {
          <button hlmBtn variant="outline" size="sm" (click)="clearFilters()">
            Clear Search
          </button>
        }
      </app-empty-state>

    <!-- Table -->
    } @else if (preAlerts.length > 0) {
      <div class="overflow-x-auto">
        <table class="w-full text-sm" aria-label="Pre-alerts">
          <thead>
            <tr class="text-[11px] font-medium uppercase tracking-wider border-b border-border-strong text-muted-foreground">
              <!-- Sort headers — reset UA button box -->
              <th class="text-left px-4 py-2.5">
                <button
                  class="appearance-none border-0 bg-transparent inline-flex items-center gap-1 cursor-pointer select-none font-medium uppercase tracking-wider text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  (click)="onSort('ladingBill')">
                  Lading Bill
                  <ng-icon hlm name="lucideArrowUpDown" class="size-3 opacity-50" />
                </button>
              </th>
              <th class="text-left px-4 py-2.5">
                <button
                  class="appearance-none border-0 bg-transparent inline-flex items-center gap-1 cursor-pointer select-none font-medium uppercase tracking-wider text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  (click)="onSort('transportCompany')">
                  Transport Company
                  <ng-icon hlm name="lucideArrowUpDown" class="size-3 opacity-50" />
                </button>
              </th>
              <th class="text-left px-4 py-2.5">Vessel No</th>
              <th class="text-left px-4 py-2.5">Route</th>
              <th class="text-left px-4 py-2.5">
                <button
                  class="appearance-none border-0 bg-transparent inline-flex items-center gap-1 cursor-pointer select-none font-medium uppercase tracking-wider text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  (click)="onSort('eta')">
                  ETA
                  <ng-icon hlm name="lucideArrowUpDown" class="size-3 opacity-50" />
                </button>
              </th>
              <th class="text-left px-4 py-2.5">
                <button
                  class="appearance-none border-0 bg-transparent inline-flex items-center gap-1 cursor-pointer select-none font-medium uppercase tracking-wider text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
                  (click)="onSort('etd')">
                  ETD
                  <ng-icon hlm name="lucideArrowUpDown" class="size-3 opacity-50" />
                </button>
              </th>
              <th class="text-right px-4 py-2.5">Weight</th>
              <th class="text-right px-4 py-2.5">Cartons</th>
              <th class="text-left px-4 py-2.5">Status</th>
              <th class="w-20"></th>
            </tr>
          </thead>
          <tbody>
            @for (preAlert of preAlerts; track preAlert.ladingBill) {
              <tr class="border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised cursor-pointer group transition-colors"
                  [attr.data-pre-alert-id]="preAlert.ladingBill"
                  [routerLink]="['/dashboard/pre-alerts', preAlert.ladingBill]">
                <td class="px-4 h-12">
                  <div class="font-mono text-[12.5px] font-medium text-foreground">
                    {{ preAlert.ladingBill }}
                  </div>
                  @if (isPreAlertBeingScanned(preAlert.ladingBill)) {
                    <div class="text-[10px] text-accent font-medium flex items-center gap-1">
                      <span class="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                      scanning
                    </div>
                  }
                </td>
                <td class="px-4 h-12 text-sm">{{ preAlert.transportCompany }}</td>
                <td class="px-4 h-12">
                  <span class="font-mono text-[12px] px-1.5 py-0.5 rounded text-xs bg-surface-raised text-muted-foreground">
                    {{ preAlert.vesselNumber }}
                  </span>
                </td>
                <td class="px-4 h-12 font-mono text-[12px] text-muted-foreground">
                  {{ preAlert.departureLocation }} → {{ preAlert.arrivalLocation }}
                </td>
                <td class="px-4 h-12">
                  <app-status-badge
                    [variant]="getEtaVariant(preAlert.eta)"
                    [label]="getEtaLabel(preAlert.eta)"
                  />
                  <div class="text-[10.5px] mt-0.5"
                       [class.text-red-700]="getETAStatus(preAlert.eta) === 'overdue'"
                       [class.text-muted-foreground]="getETAStatus(preAlert.eta) !== 'overdue'">
                    {{ formatDate(preAlert.eta) }}
                  </div>
                </td>
                <td class="px-4 h-12 text-xs tabular-nums text-muted-foreground">
                  {{ formatDate(preAlert.etd) }}
                </td>
                <td class="px-4 h-12 text-right tabular-nums text-xs">
                  {{ formatWeight(preAlert.weight) }}
                  <div class="text-[10.5px] text-muted-foreground">
                    {{ preAlert.pieces }} pcs
                  </div>
                </td>
                <td class="px-4 h-12 text-right tabular-nums font-medium">
                  {{ preAlert.cartonCount }}
                </td>
                <td class="px-4 h-12">
                  <app-status-badge
                    [variant]="getPreAlertStatusVariant(preAlert.status)"
                    [label]="preAlert.status"
                  />
                </td>
                <td class="px-4 h-12 text-right" (click)="$event.stopPropagation()">
                  <div class="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    <button hlmBtn variant="ghost" size="sm"
                            class="h-7 w-7 p-0"
                            [routerLink]="['/dashboard/pre-alerts', preAlert.ladingBill]"
                            title="View details">
                      <ng-icon hlm name="lucideEye" class="size-3.5" />
                    </button>
                    @if (!(isShopeeChecker$ | async)) {
                      <button hlmBtn variant="ghost" size="sm"
                              class="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                              (click)="deletePreAlert(preAlert)"
                              [disabled]="isLoading"
                              title="Delete Pre-Alert">
                        <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination footer -->
      <div class="px-4 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Showing
          <span class="font-medium text-foreground">
            {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, totalCount) }}
          </span>
          of
          <span class="font-medium text-foreground">{{ totalCount }}</span>
        </div>

        @if (totalPages > 1) {
          <div class="flex items-center gap-1">
            <button hlmBtn variant="ghost" size="sm"
                    class="h-7 px-2"
                    [disabled]="currentPage === 1"
                    (click)="onPageChange(1)">
              <ng-icon hlm name="lucideChevronsLeft" class="size-3.5" />
            </button>
            <button hlmBtn variant="ghost" size="sm"
                    class="h-7 px-2"
                    [disabled]="currentPage === 1"
                    (click)="onPageChange(currentPage - 1)">
              <ng-icon hlm name="lucideChevronLeft" class="size-3.5" />
            </button>
            @for (pg of getPageArray(); track pg) {
              <button hlmBtn
                      [variant]="pg === currentPage ? 'default' : 'ghost'"
                      size="sm"
                      class="h-7 w-7 p-0 text-xs"
                      (click)="onPageChange(pg)">
                {{ pg }}
              </button>
            }
            <button hlmBtn variant="ghost" size="sm"
                    class="h-7 px-2"
                    [disabled]="currentPage === totalPages"
                    (click)="onPageChange(currentPage + 1)">
              <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
            </button>
            <button hlmBtn variant="ghost" size="sm"
                    class="h-7 px-2"
                    [disabled]="currentPage === totalPages"
                    (click)="onPageChange(totalPages)">
              <ng-icon hlm name="lucideChevronsRight" class="size-3.5" />
            </button>
          </div>
        }
      </div>
    }

  </div><!-- /card -->

</div>
